import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline, AppBar } from '@mui/material';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { useTheme } from '@/contexts/ThemeContext';
import AppRoutes from '@/routes';
import { useMediaQuery } from '@mui/material';
import '@/styles/globals.css';
import Layout from './components/Layout/Layout';
import { Routes, Route } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Suspense } from 'react';

// Add theme augmentation
declare module '@mui/material/styles' {
  interface Components {
    MuiPickersDay: {
      styleOverrides: {
        root: {
          [key: string]: any;
        };
      };
    };
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          [key: string]: any;
        };
      };
    };
  }
}

function Loader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-600"></div>
    </div>
  );
}

function AppContent() {
  const { theme } = useTheme();
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const isSystemDark = theme === 'system' && prefersDarkMode;
  const isDarkMode = theme === 'dark' || isSystemDark;
  const location = useLocation();
  
  const muiTheme = createTheme({
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          'html, body': {
            WebkitTapHighlightColor: 'transparent',
            WebkitTouchCallout: 'none',
            WebkitOverflowScrolling: 'touch',
            touchAction: 'manipulation',
            overscrollBehavior: 'auto',
            height: '100%',
            margin: 0,
            padding: 0
          },
          '#root': {
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          },
          '*': {
            touchAction: 'manipulation !important',
            userSelect: 'none'
          },
          '.MuiAppBar-root': {
            position: 'sticky !important',
            top: 0,
            zIndex: 1100
          }
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: 'transparent',
            color: theme.palette.mode === 'dark' ? '#f1f5f9' : '#111827',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? '#334155' : '#f1f5f9',
            },
            '&.Mui-selected': {
              backgroundColor: '#0ea5e9 !important',
              color: '#ffffff !important',
              '&:hover': {
                backgroundColor: '#0284c7 !important',
              },
            },
          }),
        },
      },
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            color: isDarkMode ? '#f1f5f9' : '#111827',
            '& .MuiPickersCalendarHeader-label': {
              color: isDarkMode ? '#f1f5f9' : '#111827',
            },
            '& .MuiIconButton-root': {
              color: isDarkMode ? '#f1f5f9' : '#111827',
            },
          },
          labelContainer: {
            color: isDarkMode ? '#f1f5f9' : '#111827',
          },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: {
            color: isDarkMode ? '#94a3b8' : '#4b5563',
          },
          header: {
            backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
          },
        },
      },
      MuiDateCalendar: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#ffffff',
            color: theme.palette.mode === 'dark' ? '#f1f5f9' : '#111827',
            '& .MuiPickersCalendarHeader-root': {
              color: theme.palette.mode === 'dark' ? '#f1f5f9' : '#111827',
            },
            '& .MuiDayCalendar-weekDayLabel': {
              color: theme.palette.mode === 'dark' ? '#94a3b8' : '#4b5563',
            },
            '& .MuiPickersYear-yearButton': {
              color: theme.palette.mode === 'dark' ? '#f1f5f9' : '#111827',
            },
          }),
        },
      },
      MuiPickersPopper: {
        styleOverrides: {
          paper: {
            backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
            color: isDarkMode ? '#f1f5f9' : '#111827',
            '& .MuiPickersCalendarHeader-root': {
              backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
            },
          },
        },
      },
      MuiPickersLayout: {
        styleOverrides: {
          root: {
            backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
            color: isDarkMode ? '#f1f5f9' : '#111827',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-root': {
              backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
              color: isDarkMode ? '#f1f5f9' : '#111827',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: isDarkMode ? '#334155' : '#e2e8f0',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0ea5e9',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#0ea5e9',
              },
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: isDarkMode ? '#f1f5f9' : '#111827',
            '&:hover': {
              backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
            },
          },
        },
      },
    },
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#0ea5e9', // cyan-500
        light: '#38bdf8', // cyan-400
        dark: '#0284c7', // cyan-600
      },
      background: {
        default: isDarkMode ? '#0f172a' : '#ffffff',
        paper: isDarkMode ? '#1e293b' : '#ffffff',
      },
      text: {
        primary: isDarkMode ? '#f1f5f9' : '#111827',
        secondary: isDarkMode ? '#94a3b8' : '#4b5563',
      },
    },
  });

  // Check if we're on the login or auth-related pages
  const isAuthPage = location.pathname.match(/^\/(login|signup|reset-password)/);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Suspense fallback={<Loader />}>
          {isAuthPage ? (
            <AppRoutes />
          ) : (
            <Layout>
              <AppRoutes />
            </Layout>
          )}
        </Suspense>
      </LocalizationProvider>
    </MuiThemeProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
